import { useLayoutEffect, useRef, type ReactNode } from "react";

type Props = {
  /**
   * Change this to re-trigger the entrance animation — pass the route pathname
   * (for section switches) or the active tab id (for in-page tabs).
   */
  transitionKey: string;
  className?: string;
  /**
   * Skip the animation on the very first mount. Use for in-page tab panels so
   * they don't double-animate on top of the parent section's entrance.
   */
  skipInitial?: boolean;
  children: ReactNode;
};

const ENTER: Keyframe[] = [
  { opacity: 0, transform: "translateY(8px)" },
  { opacity: 1, transform: "translateY(0)" },
];

const TIMING: KeyframeAnimationOptions = {
  duration: 220,
  // easeOutExpo-ish: quick start, soft settle — the subtle, modern app feel.
  easing: "cubic-bezier(0.16, 1, 0.3, 1)",
  fill: "both",
};

/**
 * Animates its content in with a subtle fade + rise whenever `transitionKey`
 * changes — used for tab/section switches to give a native-app feel on every
 * viewport. Pure Web Animations API (no dependency, no global CSS); respects
 * `prefers-reduced-motion` and degrades to an instant swap where unsupported.
 */
export function TabTransition({
  transitionKey,
  className = "",
  skipInitial = false,
  children,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const isFirst = useRef(true);

  useLayoutEffect(() => {
    const first = isFirst.current;
    isFirst.current = false;
    if (first && skipInitial) return;

    const el = ref.current;
    if (!el || typeof el.animate !== "function") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const anim = el.animate(ENTER, TIMING);
    // `fill: "both"` keeps the last keyframe's `transform` applied indefinitely
    // once the animation finishes, which makes this element a containing block
    // for any `position: fixed` descendant (e.g. a toast) — pinning it to this
    // box instead of the viewport. Cancel once done to drop the effect; the end
    // keyframe (translateY(0), opacity 1) is visually identical to the resting
    // state, so this causes no visible jump.
    anim.onfinish = () => anim.cancel();
    return () => anim.cancel();
    // skipInitial is stable per usage; only the key should re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionKey]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}
